import { useState, useEffect, useRef } from 'react';
import {
  createOptimizedPreviewFile,
  loadImage,
  needsImageOptimization,
} from '@/utils/imageOptimization';
import { RiArrowLeftLine, RiSaveLine, RiCloseLine, RiArrowGoBackLine, RiRefreshLine, RiCropLine, RiArrowGoBackLine as RiRotateIcon, RiFlipHorizontalLine, RiZoomInLine } from '@remixicon/react';
import { Portal } from '../Portal';
import { useAssessment } from '@/hooks/useAssessment';
import { useToast } from '@/components/Toast';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { CropTool } from './CropTool';
import { RotateTool } from './RotateTool';
import { MirrorTool } from './MirrorTool';
import { ZoomTool } from './ZoomTool';
import { editorCommandButton, editorToolButton } from './editorUi';

export interface ImageHistory {
  id: string;
  imageData: string;
  timestamp: number;
}

export function EditImage() {
  const { editImage, setEditImage, setPreviewImage, uploadImage } = useAssessment();
  const { showToast } = useToast();

  const [currentImage, setCurrentImage] = useState<string>('');
  const [history, setHistory] = useState<ImageHistory[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [activeTool, setActiveTool] = useState<'crop' | 'rotate' | 'mirror' | 'zoom' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparingImage, setIsPreparingImage] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  /** Incrementa a cada abertura do Cortar — força remount do CropTool (imagem em cache não disparava onLoad / estado preso). */
  const [cropToolKey, setCropToolKey] = useState(0);
  const objectUrlRef = useRef<string | null>(null);
  /** Mesma versão redimensionada que o Preview salvaria como "original" — usada no upload. */
  const optimizedOriginalFileRef = useRef<File | null>(null);

  const fetchBlobNoStore = (signedUrl: string) => {
    return fetch(signedUrl, { cache: 'no-store' })
      .then((r) => r.blob())
      .catch((err) => {
        console.error(err);
        throw err;
      });
  };

  useEffect(() => {
    let cancelled = false;

    const prepare = async () => {
      if (!editImage?.image) return;

      // limpa URL blob anterior (se existir)
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }

      setIsPreparingImage(true);
      try {
        // Se a imagem vier como URL remota, cria um blob: URL (mesma origem),
        // evitando "tainted canvas" nas ferramentas que usam canvas (crop/rotate/etc).
        let preparedImage = editImage.image;
        const isDataUrl = preparedImage.startsWith('data:');
        const isBlobUrl = preparedImage.startsWith('blob:');

        if (!isDataUrl && !isBlobUrl) {
          const blob = await fetchBlobNoStore(preparedImage);
          const objectUrl = URL.createObjectURL(blob);
          objectUrlRef.current = objectUrl;
          preparedImage = objectUrl;
        }

        if (cancelled) return;

        const blob = await fetchBlobNoStore(preparedImage);
        const tmpLoadUrl = URL.createObjectURL(blob);
        let imageEl: HTMLImageElement;
        try {
          imageEl = await loadImage(tmpLoadUrl);
        } finally {
          URL.revokeObjectURL(tmpLoadUrl);
        }

        if (cancelled) return;

        const needsOpt = needsImageOptimization(
          blob.size,
          imageEl.naturalWidth || imageEl.width,
          imageEl.naturalHeight || imageEl.height,
        );

        let displayUrl: string;
        let originalFile: File;

        if (needsOpt) {
          originalFile = await createOptimizedPreviewFile(imageEl, editImage.photoKey, 'original');
          displayUrl = URL.createObjectURL(originalFile);
        } else {
          originalFile = new File([blob], `${editImage.photoKey}-original.jpg`, {
            type: blob.type || 'image/jpeg',
          });
          displayUrl = URL.createObjectURL(blob);
        }

        if (objectUrlRef.current && objectUrlRef.current !== displayUrl) {
          URL.revokeObjectURL(objectUrlRef.current);
        }
        objectUrlRef.current = displayUrl;
        optimizedOriginalFileRef.current = originalFile;

        setCurrentImage(displayUrl);
        setActiveTool(null);
        const originalHistory: ImageHistory = {
          id: 'original',
          imageData: displayUrl,
          timestamp: Date.now(),
        };
        setHistory([originalHistory]);
        setHistoryIndex(0);
      } catch (error) {
        console.error('Erro ao preparar imagem para edição:', error);
        showToast({
          type: 'error',
          title: 'Erro!',
          message: 'Não foi possível carregar a imagem para edição.',
          position: 'top-right',
          duration: 5000,
        });
      } finally {
        if (!cancelled) setIsPreparingImage(false);
      }
    };

    void prepare();

    return () => {
      cancelled = true;
    };
  }, [editImage]);

  useEffect(() => {
    // Animação de entrada
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      optimizedOriginalFileRef.current = null;
    };
  }, []);

  const addToHistory = (imageData: string) => {
    const newHistoryItem: ImageHistory = {
      id: Math.random().toString(36).substr(2, 9),
      imageData,
      timestamp: Date.now(),
    };

    // Remove histórico futuro se estivermos no meio
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newHistoryItem);
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setCurrentImage(imageData);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setCurrentImage(history[newIndex].imageData);
    }
  };

  const reset = () => {
    if (history.length > 0) {
      setHistoryIndex(0);
      setCurrentImage(history[0].imageData);
    }
  };

  const canUndo = historyIndex > 0;
  const canReset = historyIndex > 0;

  const handleSave = async () => {
    if (!editImage || !currentImage) {
      showToast({
        type: 'error',
        title: 'Erro!',
        message: 'Dados da imagem não disponíveis para salvar.',
        position: 'top-right',
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const originalFile = optimizedOriginalFileRef.current;
      if (!originalFile) {
        throw new Error('Imagem base não preparada');
      }

      // Sem alterações nas ferramentas: mesmo fluxo do preview — só a versão redimensionada (original).
      if (historyIndex === 0) {
        await uploadImage(originalFile, editImage.photoKey);
      } else {
        const editedBlob = await fetchBlobNoStore(currentImage);
        const editedTmp = URL.createObjectURL(editedBlob);
        try {
          const editedImageEl = await loadImage(editedTmp);
          const needsEdOpt = needsImageOptimization(
            editedBlob.size,
            editedImageEl.naturalWidth || editedImageEl.width,
            editedImageEl.naturalHeight || editedImageEl.height,
          );
          const editedFile = needsEdOpt
            ? await createOptimizedPreviewFile(editedImageEl, editImage.photoKey, 'edited')
            : new File([editedBlob], `${editImage.photoKey}-edited.jpg`, {
                type: editedBlob.type || 'image/jpeg',
              });
          await uploadImage(originalFile, editImage.photoKey, editedFile);
        } finally {
          URL.revokeObjectURL(editedTmp);
        }
      }
      
      showToast({
        type: 'success',
        title: 'Sucesso!',
        message: 'Imagem editada e salva com sucesso.',
        position: 'top-right',
        duration: 3000,
      });

      // Pequeno delay para o toast aparecer antes de fechar
      await new Promise(resolve => setTimeout(resolve, 400));

      // Fecha e volta para a lista de fotos
      setEditImage(undefined);
      setPreviewImage(undefined);
    } catch (error) {
      console.error('Erro ao salvar imagem editada:', error);
      showToast({
        type: 'error',
        title: 'Erro!',
        message: getErrorMessage(error, 'Falha ao salvar a imagem editada.'),
        position: 'top-right',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditImage(undefined);
  };

  const handleBackWithAnimation = () => {
    setIsTransitioning(true);
    
    // Aguarda a animação de saída (200ms)
    setTimeout(() => {
      setEditImage(undefined);
      setPreviewImage(editImage);
    }, 200);
  };

  if (!editImage) return null;

  return (
    <Portal>
      <div className={`fixed inset-0 z-[60] flex h-[100dvh] w-full flex-shrink-0 flex-col bg-[#f8fafc] transition-all duration-200 ease-in-out ${
        isTransitioning ? 'opacity-0 scale-95' : isAnimating ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
      }`}>
        <header className="flex h-14 w-full shrink-0 items-center gap-2 border-b border-slate-200/80 bg-white px-3 lg:px-5">
          <button
            type="button"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-xl text-slate-600 transition-colors hover:bg-slate-50"
            onClick={handleBackWithAnimation}
          >
            <RiArrowLeftLine className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">Editar imagem</p>
            <p className="truncate text-sm font-bold text-slate-900">{editImage.title}</p>
          </div>
        </header>

        <div className="flex w-full min-h-0 flex-1 flex-col overflow-hidden px-3 py-3 lg:px-6">
          <div id="edit-image" className="relative flex min-h-0 w-full flex-1 items-center justify-center">
            {isPreparingImage && (
              <div className="flex h-full w-full flex-col items-center justify-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                <span className="text-xs font-medium text-slate-500">Carregando imagem...</span>
              </div>
            )}

            {!activeTool && !isPreparingImage && (
              <div className="w-full h-full flex items-center justify-center">
              <img 
                src={currentImage} 
                alt="edit" 
                className="max-w-full max-h-full object-contain"
              />
              </div>
            )}
            
            {/* Ferramentas ocupam todo o espaço quando ativas */}
            {activeTool === 'crop' && (
              <CropTool
                key={cropToolKey}
                image={currentImage}
                onApply={(newImage: string) => addToHistory(newImage)}
                onClose={() => setActiveTool(null)}
              />
            )}
            {activeTool === 'rotate' && (
              <RotateTool
                image={currentImage}
                onApply={(newImage: string) => addToHistory(newImage)}
                onClose={() => setActiveTool(null)}
              />
            )}
            {activeTool === 'mirror' && (
              <MirrorTool
                image={currentImage}
                onApply={(newImage: string) => addToHistory(newImage)}
                onClose={() => setActiveTool(null)}
              />
            )}
            {activeTool === 'zoom' && (
              <ZoomTool
                image={currentImage}
                onApply={(newImage: string) => addToHistory(newImage)}
                onClose={() => setActiveTool(null)}
              />
            )}
          </div>
        </div>

        {!activeTool && (
        <footer className="flex w-full shrink-0 flex-col gap-2 border-t border-slate-200/80 bg-white px-3 py-3">
          <div className="flex items-center justify-center gap-1.5">
            <button
              type="button"
              onClick={() => {
                setCropToolKey((k) => k + 1);
                setActiveTool('crop');
              }}
              disabled={isPreparingImage}
              className={editorToolButton(activeTool === 'crop', isPreparingImage)}
            >
              <RiCropLine className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>Cortar</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTool('rotate')}
              disabled={isPreparingImage}
              className={editorToolButton(activeTool === 'rotate', isPreparingImage)}
            >
              <RiRotateIcon className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>Rotacionar</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTool('mirror')}
              disabled={isPreparingImage}
              className={editorToolButton(activeTool === 'mirror', isPreparingImage)}
            >
              <RiFlipHorizontalLine className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>Espelhar</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTool('zoom')}
              disabled={isPreparingImage}
              className={editorToolButton(activeTool === 'zoom', isPreparingImage)}
            >
              <RiZoomInLine className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>Zoom</span>
            </button>
          </div>
          <div className="flex items-center justify-center gap-1.5">
            <button
              type="button"
              onClick={handleSave}
              disabled={isLoading || isPreparingImage}
              className={editorCommandButton('primary', isLoading || isPreparingImage)}
            >
              <RiSaveLine className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>{isLoading ? 'Salvando...' : 'Salvar'}</span>
            </button>
            <button
              type="button"
              onClick={undo}
              disabled={!canUndo || isPreparingImage}
              className={editorCommandButton('muted', !canUndo || isPreparingImage)}
            >
              <RiArrowGoBackLine className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>Desfazer</span>
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={!canReset || isPreparingImage}
              className={editorCommandButton('muted', !canReset || isPreparingImage)}
            >
              <RiRefreshLine className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>Resetar</span>
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className={editorCommandButton('muted', false)}
            >
              <RiCloseLine className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>Sair</span>
            </button>
          </div>
        </footer>
        )}
      </div>
    </Portal>
  );
}
